import { useState } from 'react'

export default function Admissions() {
	const [status, setStatus] = useState(null)
	const [loading, setLoading] = useState(false)

	async function handleSubmit(e) {
		e.preventDefault()
		setLoading(true)
		setStatus(null)
		const form = new FormData(e.currentTarget)
		const rawPayload = Object.fromEntries(form.entries())
		const { residencyPreference, notes: extraNotes, ...rest } = rawPayload
		const payload = { ...rest }
		if (residencyPreference || extraNotes) {
			const compiledNotes = [
				residencyPreference ? `Residency Preference: ${residencyPreference}` : null,
				extraNotes?.trim() ? `Additional Notes: ${extraNotes.trim()}` : null
			].filter(Boolean).join(' | ')
			payload.notes = compiledNotes
		}
		try {
			const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080'
			const res = await fetch(`${baseUrl}/api/enrollments`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(payload)
			})
			if (!res.ok) throw new Error('Failed')
			setStatus({ type: 'success', message: 'Application submitted successfully. We will contact you soon.' })
			e.currentTarget.reset()
		} catch (err) {
			setStatus({ type: 'error', message: 'Could not submit application. Please try again.' })
		} finally {
			setLoading(false)
		}
	}

	return (
		<section className="container-gg py-12">
			<h1 className="text-3xl font-bold text-slate-900">Admissions</h1>
			<p className="mt-4 max-w-2xl text-slate-700">
				Fill out the form below to begin your child&apos;s enrollment for Nursery, Primary, or Secondary. Day school is available at every level, while boarding is offered exclusively to Secondary students.
			</p>

			<div className="mt-8 grid gap-6 md:grid-cols-3">
				<div className="rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm text-slate-700">
					<p className="font-semibold text-slate-900">Day School</p>
					<p className="mt-2">Nursery – Secondary • Flexible drop-off • After-school care • Clubs &amp; enrichment</p>
				</div>
				<div className="rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm text-slate-700">
					<p className="font-semibold text-slate-900">Boarding (Secondary)</p>
					<p className="mt-2">Available from JSS1 • House parents • Supervised prep • Weekend excursions</p>
				</div>
				<div className="rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm text-slate-700">
					<p className="font-semibold text-slate-900">Need help?</p>
					<p className="mt-2">Call +234-915 890 0059 or email greatgraceinternationalchristi@gmail.com for guidance before submitting.</p>
				</div>
			</div>

			<form onSubmit={handleSubmit} className="mt-8 grid gap-4 sm:grid-cols-2">
				<div className="sm:col-span-1">
					<label className="block text-sm font-medium text-slate-700">Student First Name</label>
					<input required name="studentFirstName" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
				</div>
				<div className="sm:col-span-1">
					<label className="block text-sm font-medium text-slate-700">Student Last Name</label>
					<input required name="studentLastName" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
				</div>
				<div className="sm:col-span-1">
					<label className="block text-sm font-medium text-slate-700">Applying To</label>
					<select required name="applyingTo" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand">
						<option value="Nursery">Nursery</option>
						<option value="Primary">Primary</option>
						<option value="Secondary">Secondary</option>
					</select>
				</div>
				<div className="sm:col-span-1">
					<label className="block text-sm font-medium text-slate-700">Prospective Class</label>
					<input required name="prospectiveClass" placeholder="e.g., Primary 3" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
				</div>
				<div className="sm:col-span-1">
					<label className="block text-sm font-medium text-slate-700">Parent/Guardian Name</label>
					<input required name="guardianName" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
				</div>
				<div className="sm:col-span-1">
					<label className="block text-sm font-medium text-slate-700">Parent Email</label>
					<input required type="email" name="guardianEmail" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
				</div>
				<div className="sm:col-span-2">
					<label className="block text-sm font-medium text-slate-700">Residency Preference</label>
					<select required name="residencyPreference" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand">
						<option value="Day School">Day School (Nursery - Secondary)</option>
						<option value="Boarding (Secondary only)">Boarding (Secondary only)</option>
						<option value="Undecided">Undecided / Need guidance</option>
					</select>
					<p className="mt-2 text-xs text-slate-500">Boarding spaces are limited to Secondary students. We will confirm availability when we contact you.</p>
				</div>
				<div className="sm:col-span-2">
					<label className="block text-sm font-medium text-slate-700">Additional Notes</label>
					<textarea name="notes" rows="4" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
				</div>
				<div className="sm:col-span-2">
					<button disabled={loading} className="btn-primary">
						{loading ? 'Submitting...' : 'Submit Enrollment Request'}
					</button>
				</div>
			</form>

			{status && (
				<p className={`mt-4 text-sm ${status.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>{status.message}</p>
			)}
		</section>
	)
}


